import { iconSvg } from './appsGrid';

/* One row archetype per capability, so the right-hand panel visibly re-draws
   itself when the chip row advances instead of just changing its wording.

   Returned as HTML strings because the live simulation paints this panel with
   plain DOM — it choreographs packets flying from the fleet, through the brain,
   into this pane, and React re-renders would fight that. */

const esc = (v) => String(v ?? '');

/* shared: the little coloured glyph every row leads with */
const glyph = (color, icon) =>
  `<span class="pico" style="background:${color}22">${iconSvg(color, icon)}</span>`;

const bar = (pct, color) =>
  `<span class="pbar"><i style="width:${Math.max(3, Math.min(100, pct))}%;background:${color}"></i></span>`;

const RENDERERS = {
  /* Org Intelligence — the original insight card */
  insight: (it) =>
    `${glyph(it.c, it.icon)}` +
    `<span class="ptxt"><span class="ptag" style="color:${it.c}">${esc(it.tag)}<span class="page">now</span></span>` +
    `<span class="pmsg">${it.msg}<button class="pact" style="color:${it.c}">${esc(it.act)}</button></span></span>`,

  /* Ask TeamGrid — a question and the answer it produced */
  chat: (it) =>
    `<span class="ptxt pchat">` +
    `<span class="pq"><span class="pyou">You</span>${esc(it.q)}</span>` +
    `<span class="pa"><img class="pbrain" src="/assets/mark-teamgrid.svg" alt="" />` +
    `<span class="pans">${it.a}<button class="pact" style="color:${it.c}">${esc(it.act)}</button></span></span></span>`,

  /* Productivity Scoring — a score, a bar and the direction it moved */
  score: (it) =>
    `<span class="ptxt pscore">` +
    `<span class="prow"><span class="pname">${esc(it.name)}</span>` +
    `<span class="pnum" style="color:${it.c}">${esc(it.score)}<i>${esc(it.delta)}</i></span></span>` +
    `${bar(it.score, it.c)}` +
    `<span class="pmeta">${esc(it.team)}</span></span>`,

  /* AI Summaries — a written page, not a metric */
  doc: (it) =>
    `<span class="ptxt pdoc">` +
    `<span class="prow"><span class="pname">${esc(it.title)}</span><span class="pwhen">${esc(it.when)}</span></span>` +
    `<span class="plines">${it.lines.map((l) => `<span>${esc(l)}</span>`).join('')}</span></span>`,

  /* Hourly Breakdown — the shape of a day as a strip */
  hours: (it) =>
    `<span class="ptxt phours">` +
    `<span class="prow"><span class="pname">${esc(it.name)}</span><span class="pmeta">${esc(it.note)}</span></span>` +
    `<span class="pstrip">${it.hours
      .map((h) => `<i class="h${h}"></i>`)
      .join('')}</span></span>`,

  /* Email Insights — a thread and how long it has waited */
  thread: (it) =>
    `<span class="pav">${esc(it.who).slice(0, 1)}</span>` +
    `<span class="ptxt pthread">` +
    `<span class="prow"><span class="pname">${esc(it.who)}</span>` +
    `<span class="pwait${it.hot ? ' hot' : ''}">${esc(it.wait)}</span></span>` +
    `<span class="pmeta">${esc(it.subject)} · ${esc(it.owner)}</span></span>`,

  /* Goals & Tasks — progress against a target */
  goal: (it) =>
    `<span class="ptxt pgoal">` +
    `<span class="prow"><span class="pname">${esc(it.title)}</span><span class="pnum" style="color:${it.c}">${esc(it.pct)}%</span></span>` +
    `${bar(it.pct, it.c)}` +
    `<span class="pmeta">${esc(it.owner)} · <b style="color:${it.c}">${esc(it.state)}</b></span></span>`,

  /* App Tracking — hours per application */
  app: (it) =>
    `<span class="ptxt papp">` +
    `<span class="prow"><span class="pname${it.warn ? ' warn' : ''}">${esc(it.app)}</span>` +
    `<span class="pnum" style="color:${it.c}">${esc(it.hrs)}h</span></span>` +
    `${bar(it.pct, it.c)}` +
    `<span class="pmeta">${esc(it.team)}</span></span>`,

  /* CRM — a deal, its stage and how long it has sat */
  deal: (it) =>
    `<span class="ptxt pdeal">` +
    `<span class="prow"><span class="pname">${esc(it.name)}</span><span class="pval">${esc(it.value)}</span></span>` +
    `<span class="pmeta"><span class="pstage" style="color:${it.c};background:${it.c}18">${esc(it.stage)}</span>` +
    `idle ${esc(it.idle)}</span></span>`,

  /* HRMS — a person and their week */
  person: (it) =>
    `<span class="pav" style="background:${it.c}22;color:${it.c}">${esc(it.name).slice(0, 1)}</span>` +
    `<span class="ptxt pperson">` +
    `<span class="prow"><span class="pname">${esc(it.name)}</span>` +
    `<span class="pdays">${it.days.map((d) => `<i class="${d ? 'on' : 'off'}"></i>`).join('')}</span></span>` +
    `<span class="pmeta">${esc(it.role)} · ${esc(it.note)}</span></span>`,
};

/** Markup for one row of the capability panel. */
export function panelRowHtml(kind, item) {
  const render = RENDERERS[kind] || RENDERERS.insight;
  return render(item);
}

export const PANEL_KINDS = Object.keys(RENDERERS);
